import { any, arrayOf, bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import selectTable from 'react-table/lib/hoc/selectTable';
import { Button } from 'reactstrap';

import { reactTableColumnShape } from '../../prop-types';
import BulkToggleControls from '../BulkToggleControls';
import Table from './Table';
import { transformTableStateToSearchParams } from './utils';
import withSelection from './with-selection';

const SelectableTable = selectTable(Table);

function SelectTable({
  data,
  count,
  selectedItems,
  onToggleSelectionMultiple,
  onToggleSelection,
  onIsSelected,
  columns,
  tableState,
  onRequest,
  queryFn,
  setSelectedItems,
  keyField,
  selectionName,
  ...rest
}) {
  const [entireDataset, setEntireDataset] = useState(false);

  const getEntireDataset = async () => {
    if (entireDataset) {
      const tableParams = {
        ...transformTableStateToSearchParams(tableState, columns),
      };
      const { filters } = tableParams;
      const tableFilters = { ...tableParams, ...filters };
      const results = await onRequest(queryFn, {
        search: { ...tableFilters, _fields: keyField },
      });
      const resultsWithIds = results.map(item => String(item[keyField]));
      setSelectedItems(resultsWithIds);
    }
  };

  // effects causes entire data set to be fetched with ids and update the selected items.
  // triggers change on select/deselect entire dataset.
  useEffect(() => {
    getEntireDataset();
    // When clear entire dataset is clicked
    if (!entireDataset) {
      // When a single row is unchecked after selecting entire dataset,
      // this handles that check.
      if (data.length === selectedItems.length) {
        onToggleSelectionMultiple(
          data.map(item => `select-${item[keyField]}`),
          false,
        );
        setSelectedItems([]);
      }
    }
    setSelectedItems(selectedItems);
  }, [entireDataset]); // eslint-disable-line react-hooks/exhaustive-deps

  // effect occurs when filters/pagesize etc is modified so that selection is not retained
  useEffect(() => {
    setEntireDataset(false);
  }, [tableState]);

  // effect occurs when selected items are modified
  useEffect(() => {
    setSelectedItems(selectedItems);
    setEntireDataset(false);
  }, [selectedItems, setSelectedItems]);

  const toggleDataset = () => {
    setEntireDataset(!entireDataset);
  };

  const hasAllItemsInPageSelected = () =>
    data.length === selectedItems.length && !entireDataset;

  const hasMoreItemsInGrid = () => data.length < count;

  const renderSelectionInfo = () =>
    data.length
      ? data.length === selectedItems.length && (
          <div className="selection-info">
            {/* Renders when all items are selected in grid and total number is not more than items in grid  */}
            {hasAllItemsInPageSelected() && (
              <div className="selection-info-sub">
                All {data.length} {selectionName} on this page are selected.
              </div>
            )}
            {/* Renders when all items are selected in grid and total number is greater than items in grid  */}
            {hasAllItemsInPageSelected() && hasMoreItemsInGrid() && (
              <div>
                <Button color="primary" onClick={toggleDataset}>
                  Select all {count} {selectionName} in the list
                </Button>
              </div>
            )}
            {/* Renders when entire dataset has been marked  */}
            {entireDataset && (
              <Button color="primary" onClick={toggleDataset}>
                Clear selection of all {count} {selectionName}
              </Button>
            )}
          </div>
        )
      : null;

  return (
    <>
      <BulkToggleControls
        onToggleAll={isSelected => {
          onToggleSelectionMultiple(
            data.map(item => `select-${item[keyField]}`),
            isSelected,
          );
        }}
        entities={data}
        activeEntities={selectedItems}
      />
      {renderSelectionInfo()}
      <SelectableTable
        {...rest}
        data={data}
        columns={columns}
        isSelected={onIsSelected}
        toggleSelection={onToggleSelection}
        keyField={keyField}
        queryFn={queryFn}
      />
    </>
  );
}

SelectTable.propTypes = {
  columns: arrayOf(reactTableColumnShape.isRequired).isRequired,
  tableState: object.isRequired, // eslint-disable-line react/forbid-prop-types
  data: arrayOf(any).isRequired, // eslint-disable-line react/forbid-prop-types
  count: number.isRequired,
  queryFn: func.isRequired,
  onRequest: func.isRequired,
  selectedItems: arrayOf(string.isRequired).isRequired,
  onToggleSelection: func.isRequired,
  onToggleSelectionMultiple: func.isRequired,
  onToggleAll: func.isRequired,
  onIsSelected: func.isRequired,
  areAllSelected: bool.isRequired,
  onStateChange: func.isRequired,
  setSelectedItems: func,
  keyField: string.isRequired,
  selectionName: string,
};

SelectTable.defaultProps = {
  selectionName: 'items',
  setSelectedItems: () => {},
};

export default withSelection({
  keyName: props => props.keyField,
})(SelectTable);
